import React, { useState, useEffect } from 'react';
import { 
  Box, Stack, Group, Title, Button, Card, Text, Badge, Modal, 
  Textarea, ScrollArea, Loader, RingProgress, ActionIcon 
} from '@mantine/core';
import { IconBriefcase, IconCheck, IconRobot, IconPlus, IconLink } from '@tabler/icons-react';

export default function JobOffersTab({ apiBaseUrl, t, setActiveTab }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [modalOpened, setModalOpened] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [cvText, setCvText] = useState('');
  
  const [evaluation, setEvaluation] = useState(null);
  const [evaluating, setEvaluating] = useState(false);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/api/announcements/public`);
      if (res.ok) {
        const data = await res.json();
        setJobs(data.filter(a => a.type === 'JOB'));
      }
    } catch (e) { 
      console.error(e); 
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const handleVerifyCv = (job) => {
    setSelectedJob(job);
    setCvText('');
    setEvaluation(null);
    setModalOpened(true);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (cvText.trim().length > 20 && selectedJob) {
        handleEvaluate();
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [cvText, selectedJob]);

  const handleEvaluate = async () => {
    if (!cvText.trim() || !selectedJob) return;
    setEvaluating(true);
    setEvaluation(null);
    try {
      const res = await fetch(`${apiBaseUrl}/api/announcements/${selectedJob.id}/evaluate-cv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cvText })
      });
      if (res.ok) {
        setEvaluation(await res.json());
      } else {
        setEvaluation({ error: 'Evaluation failed. Please try again later.' });
      }
    } catch (e) {
      console.error(e);
      setEvaluation({ error: 'Network error. Please try again later.' });
    } finally {
      setEvaluating(false);
    }
  };

  const renderCard = (a) => (
    <Card key={a.id} shadow="sm" radius="md" withBorder p="lg">
      <Group justify="space-between" mb="xs">
        <Group>
          <IconBriefcase size={20} color="blue"/>
          <Title order={5}>{a.title}</Title>
          <Badge color="blue">{t('badge_job')}</Badge>
        </Group>
        <ActionIcon color="indigo" variant="light" onClick={() => {
          const url = `${window.location.origin}/?job=${a.id}`;
          navigator.clipboard.writeText(url);
          alert("Link copied to clipboard: " + url);
        }}>
          <IconLink size={16}/>
        </ActionIcon>
      </Group>
      <Text size="sm" c="dimmed" mb="md">{a.ownerEmail} • {new Date(a.createdAt).toLocaleDateString()}</Text>
      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{a.description}</Text>
      <Group mt="md" justify="flex-end">
        <Button variant="light" color="indigo" onClick={() => handleVerifyCv(a)} leftSection={<IconRobot size={16}/>}>
          {t('verify_cv')}
        </Button>
      </Group>
    </Card>
  );

  return (
    <Box p="md" h="100%">
      <Group justify="space-between" mb="lg">
        <Title order={3}>{t('tab_job_offers')}</Title>
        {setActiveTab && (
          <Button leftSection={<IconPlus size={16}/>} onClick={() => setActiveTab('announcements')} variant="light">
            {t('add_job_offer')}
          </Button>
        )}
      </Group>

      <ScrollArea h="calc(100vh - 120px)">
        {loading ? (
          <Group justify="center" mt="xl"><Loader /></Group>
        ) : (
          <Stack gap="md">
            {jobs.map(renderCard)}
            {jobs.length === 0 && <Text c="dimmed">{t('no_job_offers')}</Text>}
          </Stack>
        )}
      </ScrollArea>

      <Modal opened={modalOpened} onClose={() => setModalOpened(false)} title={t('verify_cv_title')} size="lg">
        <Stack gap="md">
          <Text size="sm" c="dimmed">{t('verify_cv_desc')}</Text>
          <Title order={5}>{selectedJob?.title}</Title>
          
          <Textarea 
            label={t('paste_cv_label')}
            value={cvText}
            onChange={(e) => setCvText(e.target.value)}
            minRows={8}
            placeholder={t('paste_cv_placeholder')}
            disabled={evaluating}
          />
          
          {evaluating && (
            <Group justify="center" mt="md">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">AI analizuje Twoje CV...</Text>
            </Group>
          )}

          {evaluation && !evaluation.error && (
            <Card withBorder mt="md" radius="md">
              <Group align="flex-start" wrap="nowrap">
                <RingProgress
                  size={80}
                  roundCaps
                  thickness={8}
                  sections={[{ value: evaluation.score || 0, color: (evaluation.score > 70 ? 'green' : evaluation.score > 40 ? 'yellow' : 'red') }]}
                  label={
                    <Text ta="center" size="sm" fw={700}>
                      {evaluation.score}%
                    </Text>
                  }
                />
                <Box>
                  <Title order={5} mb="xs">{t('ai_feedback')}</Title>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{evaluation.feedback}</Text>
                </Box>
              </Group>
            </Card>
          )}

          {evaluation?.error && (
            <Text c="red" size="sm" mt="md">{evaluation.error}</Text>
          )}
        </Stack>
      </Modal>
    </Box>
  );
}
